import axios from 'axios';
import { ComprasEExportHoursResponse, ProjectCostCalculationResponse } from '../../types';
import { comprasEExportParentKeys } from './comprasEExportPanel';
import { PeriodReportScopeApi } from './pmoReportScope';

export const emptyProjectCostResponse = (appliedJql: string): ProjectCostCalculationResponse => ({
  applied_jql: appliedJql,
  total_hours: 0,
  total_own_hours: 0,
  total_third_party_hours: 0,
  total_own_cost_cents: 0,
  total_third_party_cost_cents: 0,
  total_cost_cents: 0,
  total_revenue_cents: 0,
  total_margin_cents: 0,
  companies: [],
  collaborators: [],
  issues: [],
  unmatched_worklogs: [],
  warnings: [],
  export_scope_incomplete: false,
  export_scope_sap_hours: null,
  export_scope_nsap_hours: null,
  export_scope_worklog_parent_keys: null,
});

const mergeCostBatch = (
  current: ProjectCostCalculationResponse,
  batch: ProjectCostCalculationResponse,
): ProjectCostCalculationResponse => ({
  ...current,
  total_hours: Number(current.total_hours || 0) + Number(batch.total_hours || 0),
  total_own_hours: Number(current.total_own_hours || 0) + Number(batch.total_own_hours || 0),
  total_third_party_hours: Number(current.total_third_party_hours || 0) + Number(batch.total_third_party_hours || 0),
  total_own_cost_cents: Number(current.total_own_cost_cents || 0) + Number(batch.total_own_cost_cents || 0),
  total_third_party_cost_cents: Number(current.total_third_party_cost_cents || 0) + Number(batch.total_third_party_cost_cents || 0),
  total_cost_cents: Number(current.total_cost_cents || 0) + Number(batch.total_cost_cents || 0),
  total_revenue_cents: Number(current.total_revenue_cents || 0) + Number(batch.total_revenue_cents || 0),
  total_margin_cents: Number(current.total_margin_cents || 0) + Number(batch.total_margin_cents || 0),
  companies: [...(current.companies || []), ...(batch.companies || [])],
  collaborators: [...(current.collaborators || []), ...(batch.collaborators || [])],
  issues: [...(current.issues || []), ...(batch.issues || [])],
  unmatched_worklogs: [...(current.unmatched_worklogs || []), ...(batch.unmatched_worklogs || [])],
  warnings: [...(current.warnings || []), ...(batch.warnings || [])],
  export_scope_incomplete: Boolean(current.export_scope_incomplete || batch.export_scope_incomplete),
});

export type CalculateProjectCostsOptions = {
  batchSize?: number;
  timeoutMs?: number;
  shouldAbort?: () => boolean;
  onBatchError?: (detail: string) => void;
  includeSubtaskWorklogs?: boolean;
  worklogStartDate?: string | null;
  worklogEndDate?: string | null;
  reportScope?: PeriodReportScopeApi;
  maxSingleIssueRetries?: number;
  /** Chamado após cada lote bem-sucedido (útil para KPIs progressivos). */
  onBatchMerged?: (merged: ProjectCostCalculationResponse) => void;
};

const isTimeoutLikeError = (error: {
  code?: string;
  message?: string;
  response?: { status?: number; data?: { detail?: string } };
}): boolean => {
  const code = String(error.code || '').toUpperCase();
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true;
  const status = Number(error.response?.status || 0);
  const detail = String(error.response?.data?.detail || '').toLowerCase();
  const message = String(error.message || '').toLowerCase();
  if (status === 502 && (detail.includes('timed out') || detail.includes('timeout'))) return true;
  if (message.includes('timed out') || message.includes('timeout')) return true;
  return false;
};

export const comprasEExportHoursToPeriodCost = (
  data: ComprasEExportHoursResponse,
): ProjectCostCalculationResponse => ({
  ...emptyProjectCostResponse(data.applied_jql || ''),
  applied_jql: data.applied_jql,
  applied_worklog_start_date: data.worklog_start_date,
  applied_worklog_end_date: data.worklog_end_date,
  total_hours: Number(data.total_hours || 0),
  warnings: data.warnings || [],
  export_scope_sold_hours: data.sold_hours ?? null,
  export_scope_incomplete: Boolean(data.incomplete),
  export_scope_sap_hours: Number(data.sap_hours || 0),
  export_scope_nsap_hours: Number(data.nsap_hours || 0),
  // null = payload sem a lista de issues; nesse caso o dashboard não restringe as linhas.
  export_scope_worklog_parent_keys: Array.isArray(data.issues)
    ? comprasEExportParentKeys(data.issues)
    : null,
});

/** Recalcula no Jira o HH Real SAP+NSAP (mesma soma das abas do export). */
export const calculateComprasEExportHours = async (
  apiUrl: string,
  worklogStartDate: string,
  worklogEndDate: string,
  timeoutMs = 300000,
): Promise<ProjectCostCalculationResponse> => {
  const response = await axios.post<ComprasEExportHoursResponse>(
    `${apiUrl}/api/projects/costs/compras-e-export-hours`,
    {
      worklog_start_date: worklogStartDate,
      worklog_end_date: worklogEndDate,
    },
    { timeout: timeoutMs },
  );
  return comprasEExportHoursToPeriodCost(response.data);
};

export const calculateProjectCostsBatched = async (
  apiUrl: string,
  appliedJql: string,
  issueKeys: string[],
  options?: CalculateProjectCostsOptions,
): Promise<{ result: ProjectCostCalculationResponse; batchErrors: string[] }> => {
  const batchSize = options?.batchSize ?? 100;
  const timeoutMs = options?.timeoutMs ?? 120000;
  const includeSubtaskWorklogs = options?.includeSubtaskWorklogs ?? true;
  const maxSingleIssueRetries = Math.max(0, Number(options?.maxSingleIssueRetries ?? 2));
  if (!issueKeys.length) {
    if (options?.reportScope === 'planilha_export_worklogs') {
      const startDate = options?.worklogStartDate;
      const endDate = options?.worklogEndDate;
      if (startDate && endDate) {
        const mapped = await calculateComprasEExportHours(apiUrl, startDate, endDate, timeoutMs);
        options?.onBatchMerged?.(mapped);
        return { result: mapped, batchErrors: [] };
      }
      const costRes = await axios.post<ProjectCostCalculationResponse>(
        `${apiUrl}/api/projects/costs/calculate`,
        {
          jql: appliedJql,
          max_results: null,
          issue_keys: [],
          discover_export_scope: true,
          include_subtask_worklogs: false,
          worklog_start_date: options?.worklogStartDate ?? null,
          worklog_end_date: options?.worklogEndDate ?? null,
          report_scope: options.reportScope,
        },
        { timeout: timeoutMs },
      );
      options?.onBatchMerged?.(costRes.data);
      return { result: costRes.data, batchErrors: [] };
    }
    return { result: emptyProjectCostResponse(appliedJql), batchErrors: [] };
  }

  const issueKeyBatches: string[][] = [];
  for (let i = 0; i < issueKeys.length; i += batchSize) {
    issueKeyBatches.push(issueKeys.slice(i, i + batchSize));
  }

  let mergedCost: ProjectCostCalculationResponse | null = null;
  const batchErrors: string[] = [];
  const pendingBatches: Array<{ keys: string[]; retryCount: number }> = issueKeyBatches.map((keys) => ({
    keys,
    retryCount: 0,
  }));

  while (pendingBatches.length) {
    if (options?.shouldAbort?.()) {
      throw new Error('Cálculo de custos cancelado.');
    }
    const currentBatch = pendingBatches.shift();
    if (!currentBatch) break;
    const batchKeys = currentBatch.keys;
    try {
      const costRes = await axios.post<ProjectCostCalculationResponse>(
        `${apiUrl}/api/projects/costs/calculate`,
        {
          jql: appliedJql,
          max_results: Math.max(1, Math.min(5000, batchKeys.length)),
          issue_keys: batchKeys,
          include_subtask_worklogs: includeSubtaskWorklogs,
          worklog_start_date: options?.worklogStartDate ?? null,
          worklog_end_date: options?.worklogEndDate ?? null,
          report_scope: options?.reportScope ?? null,
        },
        { timeout: timeoutMs },
      );
      const batch = costRes.data;
      if (!mergedCost) {
        mergedCost = {
          ...batch,
          companies: [...(batch.companies || [])],
          collaborators: [...(batch.collaborators || [])],
          issues: [...(batch.issues || [])],
          unmatched_worklogs: [...(batch.unmatched_worklogs || [])],
          warnings: [...(batch.warnings || [])],
        };
      } else {
        mergedCost = mergeCostBatch(mergedCost, batch);
      }
      options?.onBatchMerged?.(mergedCost);
    } catch (batchErr: unknown) {
      const ax = batchErr as {
        code?: string;
        message?: string;
        response?: { status?: number; data?: { detail?: string } };
      };
      const isTimeout = isTimeoutLikeError(ax);
      if (isTimeout && batchKeys.length > 1) {
        const splitAt = Math.ceil(batchKeys.length / 2);
        const firstHalf = batchKeys.slice(0, splitAt);
        const secondHalf = batchKeys.slice(splitAt);
        if (firstHalf.length) pendingBatches.unshift({ keys: firstHalf, retryCount: currentBatch.retryCount + 1 });
        if (secondHalf.length) pendingBatches.unshift({ keys: secondHalf, retryCount: currentBatch.retryCount + 1 });
        continue;
      }
      if (isTimeout && batchKeys.length === 1 && currentBatch.retryCount < maxSingleIssueRetries) {
        pendingBatches.push({ keys: batchKeys, retryCount: currentBatch.retryCount + 1 });
        continue;
      }
      const statusDetail = ax.response?.status ? `HTTP ${ax.response.status}` : '';
      const apiDetail = ax.response?.data?.detail || '';
      const transportDetail = ax.code === 'ECONNABORTED' ? 'timeout no lote (120s)' : (ax.message || 'falha no lote');
      const detail = [statusDetail, apiDetail || transportDetail, `issues no lote: ${batchKeys.length}`]
        .filter(Boolean)
        .join(' - ');
      batchErrors.push(detail);
      options?.onBatchError?.(detail);
    }
  }

  if (!mergedCost) {
    throw new Error(batchErrors[0] || 'Falha ao calcular custos para os itens do escopo.');
  }

  return { result: mergedCost, batchErrors };
};
